import React, { useState, useEffect, useCallback } from 'react'
import BuilderMap from '../../../BuilderMap/container/BuilderMap'
import { BUILDER_MAP_MODE } from '../../../BuilderMap/const'
import { updateOverlayStatus } from './util'
import { getPolygonCentreAndDelta } from '../../../../shared/libraries/geo'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import LoadingIndicator from '../../../../Components/LoadingIndicator/LoadingIndicator'

/**
 * Component that renders map for building campaign's city plans and LB missions by toggling existing overlays
 * @param {function} props.onUpdate
 * @param {string} props.countryCode Campaign's country code
 * @param {array} props.existingBoundaries Existing mission's overlays, used for hiding overlays if the one already exists
 * @param {array} props.cities Cities that will be used for map centering
 * @return {JSX.Element} <CampaignBuilderMap />
 */
const CampaignBuilderMap = ({
  onUpdate,
  countryCode,
  existingBoundaries,
  cities,
  prebuiltOverlays,
  loading,
}) => {
  const [selectedOverlaysMap, setSelectedOverlaysMap] = useState({})
  const [mapPositionOptions, setMapPositionOptions] = useState([])
  const [mapPosition, setMapPosition] = useState(null)
  const [setModalVisibleStatus, setModalControls] = useState(null)
  const [cityId, setCityId] = useState(null)

  useEffect(() => {
    if (!loading) setSelectedOverlaysMap({})
  }, [loading])

  useEffect(() => {
    const newMapPositionOptions = cities
      .reduce((acc, city) => {
        if (city.country.code === countryCode) {
          acc.push({
            id: city.id,
            label: city.name,
            value: {
              lat: city.location.latitude,
              lng: city.location.longitude,
            },
          })
        }
        return acc
      }, [])
      .sort((a, b) => (a.label + '').localeCompare(b.label + ''))

    setMapPositionOptions(newMapPositionOptions)

    if (newMapPositionOptions.length > 0 && !mapPosition) {
      setMapPosition(newMapPositionOptions[0].value)
      setCityId(newMapPositionOptions[0].id)
    }
  }, [cities, countryCode])

  useEffect(() => {
    onUpdate(selectedOverlaysMap)
  }, [selectedOverlaysMap])

  // Mark overlay as active or inactive
  const handleOverlayToggled = overlay =>
    setSelectedOverlaysMap(selectedOverlaysMap => {
      if (selectedOverlaysMap[overlay.customId]) {
        const isActive = false
        updateOverlayStatus(overlay, isActive)
        // Hide modal
        setModalVisibleStatus && setModalVisibleStatus(false)
        // Copy
        const updatedSelectedOverlaysMap = { ...selectedOverlaysMap }
        // Remove untoggled overlay
        delete updatedSelectedOverlaysMap[overlay.customId]
        // Set
        return updatedSelectedOverlaysMap
      }

      const isActive = true
      updateOverlayStatus(overlay, isActive)
      return {
        ...selectedOverlaysMap,
        [overlay.customId]: overlay,
      }
    })

  /**
   * Side processing of overlays that we want to display
   * <BuilderMap /> will call this method on the overlays that are ready to be displayed
   * So all specific logic that is specific to current component that uses it could be done
   * outside of the <BuilderMap /> to keep it encapsulated
   * @param {Array} rawOverlaysReceived Overlays that were received and formatted by <BuilderMap />
   * @returns {Array} Overlays that will be displayed
   */
  const processOverlays = useCallback(
    rawOverlays => {
      const rawOverlaysCombined = [
        ...(rawOverlays || []),
        ...(prebuiltOverlays || []),
      ]

      if (!rawOverlaysCombined.length) return []
      if (!Array.isArray(existingBoundaries)) return rawOverlaysCombined

      const existingCenters = existingBoundaries.map(({ boundary }) => {
        const { latitude, longitude } = getPolygonCentreAndDelta(boundary)
        return { latitude, longitude }
      })

      const processedOverlays = rawOverlaysCombined.map(overlay => {
        const { latitude, longitude } = getPolygonCentreAndDelta(
          overlay && overlay.paths && overlay.paths[0] && overlay.paths[0].map
            ? overlay.paths[0].map(({ lat, lng }) => ({
                latitude: lat,
                longitude: lng,
              }))
            : [],
        )

        const shouldDisplay = existingCenters.reduce(
          (shouldDisplay, existingCenter) => {
            const {
              latitude: latitudeExistingCenter,
              longitude: longitudeExistingCenter,
            } = existingCenter

            if (
              latitudeExistingCenter == latitude &&
              longitudeExistingCenter == longitude
            ) {
              return false
            }
            return shouldDisplay
          },
          true,
        )

        if (!shouldDisplay) {
          overlay.color = '#bdbdbd'
          overlay.mapPolygonProps.clickable = false
        }

        return overlay
      })

      return [...existingBoundaries, ...processedOverlays]
    },
    [existingBoundaries],
  )

  const defaultMapPosition = mapPositionOptions[0]
    ? mapPositionOptions[0].value
    : {
        lat: 0,
        lng: 0,
      }

  if (mapPositionOptions.length === 0) {
    return <LoadingIndicator />
  }

  const handleCityChange = e => {
    const cityId = mapPositionOptions.find(
      option => JSON.stringify(option.value) === JSON.stringify(e.target.value),
    ).id

    setCityId(cityId)
    setMapPosition(e.target.value)
    setModalVisibleStatus && setModalVisibleStatus(false)
  }

  return (
    <section>
      <header>
        <FormControl margin="normal">
          <InputLabel>City</InputLabel>
          <Select
            id="mapPosition-select"
            name="mapPosition"
            value={mapPosition}
            onChange={handleCityChange}
            cypress_id="campaign-details-campaign-map-city-dropdown"
          >
            {mapPositionOptions
              .sort((a, b) => (a.label + '').localeCompare(b.label + ''))
              .map(option => (
                <MenuItem
                  value={option.value}
                  key={option.id}
                  cypress_id="campaign-details-campaign-map-city-dropdown-item"
                >
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </header>
      <BuilderMap
        key={JSON.stringify(existingBoundaries)}
        mode={BUILDER_MAP_MODE.TOGGLE}
        onOverlayClick={handleOverlayToggled}
        selectedOverlaysMap={selectedOverlaysMap}
        setSelectedOverlaysMap={setSelectedOverlaysMap}
        countryCode={countryCode}
        processOverlays={processOverlays}
        mapPosition={mapPosition}
        defaultMapPosition={defaultMapPosition}
        setModalControls={f => setModalControls(() => f)}
        skipHouseholdsDataOverview={true}
        canUsePolygonSplit={true}
        filterByCityIdDistributionZone={cityId}
      />
    </section>
  )
}

export default CampaignBuilderMap
